import { cn } from '@utils/cn';
import { ReactNode } from 'react';

import styles from './framing.module.css';

export interface FrameTab<K extends string> {
  key: K;
  label: ReactNode;
}

interface FrameTabsProps<K extends string> {
  tabs: Array<FrameTab<K>>;
  activeKey: K;
  onChange: (key: K) => void;
  /** Prefix for the tab/panel ids; panels use `${idPrefix}-panel-${key}`. */
  idPrefix: string;
  /** Stretch tabs to share the full width instead of sitting inline. */
  fullWidth?: boolean;
  /** Always use the mobile type size (e.g. in the checkout drawer). */
  compact?: boolean;
}

/** Underlined tab strip shared by the framing components. */
export const FrameTabs = <K extends string>({
  activeKey,
  compact = false,
  fullWidth = false,
  idPrefix,
  onChange,
  tabs,
}: FrameTabsProps<K>) => (
  <div
    className={cn(
      styles.UiFrameTabs,
      fullWidth ? 'grid grid-cols-[repeat(auto-fit,minmax(0,1fr))]' : 'flex',
    )}
    role="tablist"
  >
    {tabs.map(({ key, label }) => (
      <button
        key={key}
        // Only the active panel is rendered, so only it can be referenced.
        aria-controls={
          activeKey === key ? `${idPrefix}-panel-${key}` : undefined
        }
        aria-selected={activeKey === key}
        className={cn(styles.UiFrameTabs__tab, !fullWidth && 'px-3 first:pl-0')}
        data-compact={compact}
        id={`${idPrefix}-tab-${key}`}
        onClick={() => onChange(key)}
        role="tab"
        type="button"
      >
        {label}
      </button>
    ))}
  </div>
);
